import { createAction } from 'redux-actions';
import * as types from '../constants/Package';
import { userDataInitialAction, userDataResetLoaderAction } from './UserData';
import { appUpdateStateAction } from './App';
import { getSectionPageStepFromHash, isHashFirstStep } from '../utilities/section/sectionUtils';
import { actionHandler, scrollToTop } from '../utilities/form/formUtils';
import { consentService } from '../utilities/api/consent';

// Function to replace the url with the latest hash details as query parameter.
export const replaceUrl = (currentHash, windowHash = window.location.hash) =>
  `${windowHash.split('?')[0]}?${currentHash.replace(/\//g, '-')}`;

export const handleUrlChange = (state) => {
  /* eslint-disable no-restricted-globals */
  const position = Number(history.state);
  const { section: { currentHash, packageData: { disableUrlChange } } } = state;
  if (!disableUrlChange) {
    history.replaceState(position, currentHash, replaceUrl(currentHash));
  }
  else if(disableUrlChange &&
    window.location.href.includes('state')){
      if(!state.userData.referer) {
        history.replaceState(position, currentHash, window.location.href);
      }
      else
        history.replaceState(position, currentHash, state.userData.referer);
    }
  sessionStorage.setItem('stateLastShown', position);
  /* eslint-enable no-restricted-globals */
};
// Actions
// Initial actions.
export const packageInitial = createAction(types.PACKAGE_INITIAL);
export const packageInitialAction = (payload) => (dispatch, getState) => {
  // Ensure history is correct.
  const step = dispatch(packageInitial(payload));
  const state = getState();
  handleUrlChange(state);

  return step;
};

export const packageReceive = createAction(types.PACKAGE_RECEIVE);

export function isPackageReceiveAfterLogin() {
  return window.location.href.includes('state=');
}

export const packageReceiveAction = (payload, mock = false) => (dispatch, getState) => {
  // Ensure history is correct.
  if (isPackageReceiveAfterLogin()) {
    const stateData = JSON.parse(sessionStorage.getItem('stateData'));
    payload = { ...payload, lastState: stateData};
    sessionStorage.removeItem('stateData');
  }
  const step = dispatch(packageReceive(payload));
  const state = getState();
  const { section: { packageData } } = state;
  const packageDataParam = (payload.lastState && payload.lastState != null) ? payload : packageData;

  // If a lastState is detected, the page will scroll down to the widget.
  // The primary use of this, is to scroll to the widget, after MitID login
  if(payload.lastState && payload.lastState != null){
    scrollToTop(state)
  }

  // Set relevant user data here.
  dispatch(userDataInitialAction(packageDataParam));
  handleUrlChange(state);
  // Check if there are debug options. These should be set in App state.
  const { initialValues: { debugServices, debugErrors, consentTypes, consentUsage, globalPartyKey,
    consentEnabled } } = packageData;
  if (consentEnabled && consentEnabled !== 'undefined' && !mock && !(payload.lastState && payload.lastState != null)) {
    dispatch(consentService.getConsentTypesService(consentTypes, consentUsage, globalPartyKey));
  }
  if (debugServices) {
    dispatch(appUpdateStateAction({
      debugServices,
    }));
  }
  if (debugErrors) {
    dispatch(appUpdateStateAction({
      debugErrors,
    }));
  }
  return step;
};

export const packageRequest = createAction(types.PACKAGE_REQUEST);
export const packageRequestAction = (payload) => (dispatch) => (
  dispatch(packageRequest(payload))
    .then(() => (
      dispatch(packageReceiveAction())
    ))
);

export const packageUpdate = createAction(types.PACKAGE_UPDATE);
export const packageUpdateAction = (payload) => (dispatch) => (
  dispatch(packageUpdate(payload))
);

// Section actions.
export const nextSection = createAction(types.SECTION_NEXT);
export const nextSectionAction = () => (dispatch, getState) => {
  const { userData } = getState();
  return dispatch(nextSection({
    ...userData,
  }));
};

// Page actions.
export const nextPage = createAction(types.PAGE_NEXT);
export const nextPageAction = () => (dispatch, getState) => {
  const { userData } = getState();
  const page = dispatch(nextPage({
    ...userData,
  }));

  // Check if we need to update next page.
  const state = getState();
  if (state.section.nextSection) {
    dispatch(nextSection({
      ...userData,
    }));
  }

  return page;
};

// Step actions.
export const nextStep = createAction(types.STEP_NEXT);
export const nextStepAction = () => (dispatch, getState) => {
  const { userData } = getState();

  // Move the field of view to the top of the widget
  scrollToTop(userData);

  // Stop if any idleAction
  if (window.idleAction) {
    window.idleAction.stop();
  }

  // Always Reset loader.
  dispatch(userDataResetLoaderAction());

  // First do update of next step.
  const step = dispatch(nextStep({
    ...userData,
  }));

  // Check if we need to update next page.
  let state = getState();
  if (state.section.currentPageData.nextPage) {
    dispatch(nextPage({
      ...userData,
    }));
  }

  // Check of we need to update next section.
  state = getState();
  if (state.section.nextSection) {
    dispatch(nextSection({
      ...userData,
    }));
    // Get the updated state hash after new section.
    state = getState();
  }
  sessionStorage.setItem('action', 'next');
  handleUrlChange(state);
  return step;
};

export const prevStep = createAction(types.STEP_PREV);
export const prevStepAction = () => (dispatch, getState) => {
  /* eslint-disable no-restricted-globals */
  // Check if we're allowed to perform a previous step. If not we just replace the current history.
  let state = getState();

  // Move the field of view to the top of the widget
  scrollToTop(state);

  // Stop if any idleAction
  if (window.idleAction) {
    window.idleAction.stop();
  }
  sessionStorage.setItem('action', 'prev');
  if (isHashFirstStep(state.section)) {
    const position = Number(history.state);
    handleUrlChange(state);
    sessionStorage.setItem('stateLastShown', position);
    return {};
  }

  const { userData, app: { error, errorData } } = getState();
  if (error) {
    dispatch(appUpdateStateAction({ error: false }));
    dispatch(userDataResetLoaderAction());
    dispatch(packageUpdateAction({ errorData }));
  }
  const step = dispatch(prevStep({
    ...userData,
  }));

  // step.nextSectionData;

  // Find out if the page we go back to is a Loader. We cannot go back to those. So we reset.
  state = getState();
  const { section: { currentHash, packageData, nextSectionData, nextPageData } } = state;
  const hashData = getSectionPageStepFromHash(currentHash, packageData);

  if (nextSectionData && nextSectionData.backButtonConfig) {
    actionHandler(nextSectionData.backButtonConfig.actions, null, null, dispatch, userData);
  }

  if (nextPageData && nextPageData.backButtonConfig) {
    actionHandler(nextPageData.backButtonConfig.actions, null, null, dispatch, userData);
  }

  if (hashData) {
    const { page: { component: { componentName } } } = hashData;
    if (componentName === 'Loader') {
      dispatch(userDataResetLoaderAction());
      return dispatch(packageInitialAction());
    }
  }

  handleUrlChange(state);
  return step;
};

// Element Actions.
export const elementToggler = createAction(types.ELEMENT_TOGGLE);
export const elementTogglerAction = (payload) => (dispatch) => {
  dispatch(elementToggler(payload));
};